import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../../../../components/navbar';
import Footer from '../../../../components/footer';
import Button from '../../../../components/Button';
import Dropdown from '../../../../components/Dropdown';
import FileInput from '../../../../components/FileInput';
import { useConsultation } from '../../../../context/ConsultationContext';
import { handleTreatmentServiceNavigation } from '../components/treatmentNavigationHelper';
import bpStyles from '../TreatmentTypes/css/BloodPressure.module.css';
import styles from './css/WellnessScreening.module.css';
import { Check, Syringe, RefreshCcw, Send } from 'lucide-react';

const WellnessScreening = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { consultationData, updateConsultationData, getStepDraft, setStepDraft } = useConsultation();

    const patient = location?.state?.patient || consultationData?.patient || {};
    const patientName = `${patient?.firstname || ""} ${patient?.surname || ""}`.trim() || "Thandiwe Ndlovu";

    const [screeningType, setScreeningType] = useState('');
    const [batchNumber, setBatchNumber] = useState('');
    const [dateAdministered, setDateAdministered] = useState('');
    const [notes, setNotes] = useState('');
    const [consentFormFile, setConsentFormFile] = useState(null);
    const [nonSmokerDeclarationFile, setNonSmokerDeclarationFile] = useState(null);
    const [vaccineConsentFile, setVaccineConsentFile] = useState(null);

    const handleVerifySync = () => {
        console.log('Verify & Sync Medical Aid clicked');
    };

    const handleSubmit = () => {
        handleTreatmentServiceNavigation({
            location,
            navigate,
            consultationData,
            updateConsultationData,
            getStepDraft,
            setStepDraft,
            serviceData: {
                wellness_screening: {
                    screeningType,
                    batchNumber,
                    dateAdministered,
                    notes,
                    consentFormFile,
                    nonSmokerDeclarationFile,
                    vaccineConsentFile,
                },
            },
        });
    };

    useEffect(() => {
        document.body.classList.add(bpStyles.hideSidebar);
        return () => {
            document.body.classList.remove(bpStyles.hideSidebar);
        };
    }, []);

    return (
        <div className={styles.page}>
            <Navbar />

            <main className={styles.main}>
                <div className={styles.header}>
                    <div className={styles.headerTitle}>Associations &amp; Screenings</div>
                    <div className={styles.headerSubtitle}>Medical aid verification and wellness screening for {patientName}</div>
                </div>

                <div className={styles.content}>
                    <section className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div className={styles.cardHeaderTitle}>Medical Aid Integration</div>
                        </div>

                        <div className={styles.integrationGrid}>
                            <div className={styles.integrationItem}>
                                <div className={styles.integrationLabel}>Medical Aid</div>
                                <div className={styles.integrationValue}>Discovery Health</div>
                            </div>
                            <div className={styles.integrationItem}>
                                <div className={styles.integrationLabel}>Member Number</div>
                                <div className={styles.integrationValue}>DH-882911</div>
                            </div>
                        </div>

                        <div className={styles.integrationActions}>
                            <Button
                                type="button"
                                onClick={handleVerifySync}
                                className={styles.primaryActionButton}
                            >
                                <RefreshCcw size={16} />
                                Verify &amp; Sync Medical Aid
                            </Button>
                        </div>
                    </section>

                    <section className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div className={styles.cardHeaderIcon} aria-hidden="true">
                                <Syringe />
                            </div>
                            <div className={styles.cardHeaderTitle}>Screening Workflow</div>
                        </div>

                        <div className={styles.formSection}>
                            <div className={styles.fieldBlock}>
                                <div className={styles.fieldLabel}>Screening Type</div>
                                <Dropdown
                                    name="screeningType"
                                    value={screeningType}
                                    onChange={setScreeningType}
                                    options={['Flu Vaccine', 'HIV Rapid Test', 'Glucose Screening', 'Cholesterol Screening', 'Blood Pressure Check']}
                                    placeholder="Select..."
                                    className={styles.control}
                                    alignLeft
                                />
                            </div>

                            <div className={styles.twoColRow}>
                                <div className={styles.fieldBlock}>
                                    <div className={styles.fieldLabel}>Batch Number</div>
                                    <input
                                        type="text"
                                        value={batchNumber}
                                        onChange={(e) => setBatchNumber(e.target.value)}
                                        className={styles.control}
                                        placeholder="e.g., FV-2026-A41"
                                    />
                                </div>
                                <div className={styles.fieldBlock}>
                                    <div className={styles.fieldLabel}>Date Administered</div>
                                    <input
                                        type="date"
                                        value={dateAdministered}
                                        onChange={(e) => setDateAdministered(e.target.value)}
                                        className={styles.control}
                                    />
                                </div>
                            </div>

                            <div className={styles.fieldBlock}>
                                <div className={styles.fieldLabel}>Notes</div>
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    className={styles.textarea}
                                    placeholder="Any relevant notes..."
                                    rows={4}
                                />
                            </div>

                            <div className={styles.documentsPanel}>
                                <div className={styles.documentsTitle}>Documents</div>
                                <div className={styles.documentsChips}>
                                    <div className={styles.docChip}>
                                        <Check size={14} />
                                        Consent Form
                                    </div>
                                    <div className={styles.docChip}>
                                        <Check size={14} />
                                        Non-Smoker Declaration
                                    </div>
                                    <div className={styles.docChip}>
                                        <Check size={14} />
                                        Vaccine Consent
                                    </div>
                                </div>
                                <div className={styles.documentsHint}>Digital forms will be presented for completion before submission</div>

                                <div className={styles.hiddenInputs}>
                                    <FileInput
                                        label="Consent Form"
                                        name="consentForm"
                                        id="consentForm"
                                        value={consentFormFile}
                                        onChange={(e) => setConsentFormFile(e.target.files?.[0] || null)}
                                        accept="application/pdf,image/*"
                                    />
                                    <FileInput
                                        label="Non-Smoker Declaration"
                                        name="nonSmokerDeclaration"
                                        id="nonSmokerDeclaration"
                                        value={nonSmokerDeclarationFile}
                                        onChange={(e) => setNonSmokerDeclarationFile(e.target.files?.[0] || null)}
                                        accept="application/pdf,image/*"
                                    />
                                    <FileInput
                                        label="Vaccine Consent"
                                        name="vaccineConsent"
                                        id="vaccineConsent"
                                        value={vaccineConsentFile}
                                        onChange={(e) => setVaccineConsentFile(e.target.files?.[0] || null)}
                                        accept="application/pdf,image/*"
                                    />
                                </div>
                            </div>

                            <div className={styles.formActions}>
                                <Button
                                    type="button"
                                    onClick={handleSubmit}
                                    className={styles.primaryActionButton}
                                >
                                    <Send size={16} />
                                    Submit to Medical Aid
                                </Button>
                            </div>
                        </div>
                    </section>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default WellnessScreening;